import i18n from 'i18next'
import {initReactI18next, useTranslation} from 'react-i18next'
import markdownJsx from 'i18next-markdown-jsx-plugin'
import pluralize from 'pluralize'
import utils from '@eitje/utils'
import {date, moment} from '@eitje/utils'
import {t} from '@eitje/bridge'
import _ from 'lodash'
import 'moment/locale/nl'
import {Children, createElement, isValidElement, useCallback} from 'react'

const MarkdownAnchor = ({children, href, rel, target, ...props}) => {
	const child = Children.toArray(children)[0]
	const hasSingleChild = Children.count(children) === 1
	// Raw HTML anchors with a bare URL as their label get parsed twice by markdown-to-jsx:
	// first as the outer <a>, then again as an auto-linked URL child. Collapse that shape
	// so the clickable link keeps the outer anchor props, such as target="_blank".
	// We had issues with URL's inside anchors, causing the page to be opened in the current tab always, despite the target="_blank" prop.
	// This is a workaround to ensure that the link opens in a new tab if the outer anchor has a target="_blank" prop.
	const isNestedAnchor = hasSingleChild && isValidElement(child) && child.type === MarkdownAnchor && child.props.href === href
	const linkTarget = target || (isNestedAnchor ? child.props.target : undefined)
	const linkRel = linkTarget === '_blank' && !rel ? 'noopener noreferrer' : rel

	return createElement(
		'a',
		{
			...props,
			href,
			rel: linkRel,
			target: linkTarget,
		},
		isNestedAnchor ? child.props.children : children,
	)
}

const getNthDay = day => {
	const m = date()
	m.date(day)
	return m.format('Do')
}

const markdownOpts = {
	overrides: {
		p: {
			props: {
				className: 'markdown-t',
			},
		},
		a: {
			component: MarkdownAnchor,
		},
	},
}

const updateMomentLocale = locale => {
	moment.updateLocale(locale, {
		week: {
			dow: 1,
		},
	})
}

function format(value, format, lng) {
	if (format == 'dateIndef') return value ? utils.fullHumanDate(value, {dayname: false}) : t('indefinite')
	if (format == 'date') return utils.fullHumanDate(date(value), {dayname: false})
	if (format == 'time') return date(value).format('HH:mm')

	if (format == 'dateTime') return date(value).format('DD-MM-YYYY HH:mm')

	if (format == 'dayMonthTime') return date(value).format('DD MMMM, HH:mm')

	if (format == 'dayMonthTimeShort') return date(value).format('DD MMM, HH:mm')

	if (format == 'dayMonth') return date(value).format('D MMM')

	if (format === 'fullDate') return utils.fullHumanYearDate(value)
	if (format == 'countOrNull') return value && value > 0 ? value : null
	if (format === 'pluralize') return pluralize(value)
	if (format === 'capitalize') return utils.capitalize(value)
	if (format === 'uppercase') return value.toUpperCase()
	if (format === 'nth') return getNthDay(value)
	if (format === 'toHours') return utils.minutesToString(value)
	if (format === 'signedToHours') return utils.minutesToString(value, {sign: true})
	if (format === 'unsignedToHours') return utils.minutesToString(value, {sign: false})

	return value
}

const _ignoreTrans = (t, text, opts) => {
	if (_.isArray(text)) {
		const val = text.find(textItem => {
			const translated = t(textItem)
			return translated != textItem
		})

		return val
	}

	const val = t(text)
	if (val == text && opts?.defaultValue) return opts?.defaultValue
	return text
}

const _prodT = (t, key, opts = {}) => {
	if (!key) return null

	if (_.isString(opts)) {
		opts = {defaultValue: opts}
	}
	opts = {...opts, globals: window.globals}

	const count = opts?.count
	const rest = {...opts}
	delete rest.count

	// we've disabled count-based pluralization because it doesn't play well with interpolation..

	// 1) Count-based pluralization
	// if (count != null && count > 1) {
	// 	const pluralKey = key + '_plural'
	// 	if (!i18n.exists(pluralKey)) {
	// 		const singular = t(key, {count, ...rest})
	// 		return pluralize(singular, count)
	// 	}
	// }

	// 2) Literal '_plural' fallback
	if (count == null && key.endsWith && key.endsWith('_plural')) {
		if (!i18n.exists(key)) {
			const baseKey = key.slice(0, -'_plural'.length)
			const singular = t(baseKey, rest)
			return pluralize(singular)
		}
	}

	return t(key, opts)
}

const _devT = (t, text, opts) => {
	if (window.ignoreTranslations) return _ignoreTrans(t, text, opts)
	const val = _prodT(t, text, opts)
	// if (opts && opts['count'] && val) {
	//   const val = i18n.t(text, opts)
	//   return val && !val.includes(' ') ? pluralize(val) : val
	// }

	if (val) return val

	return text
}

// global env instance
let globalEnv

export const initI18n = ({resources, fallbackLng = 'en', env, middlewareFn = a => a, configOptions}) => {
	globalEnv = env
	const languages = Object.keys(resources)

	const getLanguage = () => {
		const lng = i18n.language
		if (languages.includes(lng)) return lng
		return languages.find(l => lng.includes(l)) || 'nl'
	}

	middlewareFn(i18n) // this allows consumers to add plugins to i18n

	i18n
		.use(initReactI18next)
		.use(markdownJsx)
		.init({
			resources,
			fallbackLng,
			debug: false,
			nsSeparator: false,
			...configOptions,
			interpolation: {
				format,
				skipOnVariables: false,
				escapeValue: false, // not needed for react as it escapes by default
			},
		})

	const devT = (text, opts) => {
		// bind t to the i18n instance method, to ensure it's always called with the correct context
		const t = i18n.t.bind(i18n)
		return _devT(t, text, opts)
	}

	const prodT = (key, opts = {}) => {
		// bind t to the i18n instance method, to ensure it's always called with the correct context
		const t = i18n.t.bind(i18n)
		return _prodT(t, key, opts)
	}

	const t = env === 'production' ? prodT : devT
	const mdT = (text, opts) => t(text, {postProcess: 'markdown-jsx', ..._.merge({}, markdownOpts, opts)})

	const parseT = T => {
		if (!T) return
		if (_.isString(T)) T = {key: T}

		const {key, ...opts} = T
		return t(key, opts)
	}

	const changeLanguage = locale => {
		if (!locale) locale = 'nl'
		i18n.changeLanguage(locale)
		updateMomentLocale(locale)
	}

	// set moment locale to current language
	updateMomentLocale(getLanguage())

	return {i18n, languages, t, mdT, parseT, getLanguage, changeLanguage}
}

export const useT = () => {
	const {t, ...rest} = useTranslation()

	const customT = useCallback(
		(key, opts) => {
			return globalEnv === 'production' ? _prodT(t, key, opts) : _devT(t, key, opts)
		},
		[t],
	)

	return {t: customT, ...rest}
}

export {i18n}
